import { useState, useCallback } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { galleryItems, eventItems, livestockItems } from '@/mocks/gallery';
import type { GalleryItem } from '@/mocks/gallery';

type TabKey = 'events' | 'livestock';

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'events', label: 'Events', icon: 'ri-calendar-event-line' },
  { key: 'livestock', label: 'Livestock', icon: 'ri-leaf-line' },
];

export default function GallerySection() {
  const { ref, isVisible } = useScrollAnimation();
  const [activeTab, setActiveTab] = useState<TabKey>('events');
  const [lightbox, setLightbox] = useState<number | null>(null);

  const tabItems: Record<TabKey, GalleryItem[]> = {
    events: eventItems,
    livestock: livestockItems,
  };

  const currentItems = tabItems[activeTab];

  const activeLightbox = lightbox !== null
    ? galleryItems.find((g) => g.id === lightbox) ?? null
    : null;

  const handlePrev = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeLightbox == null) return;
    const idx = currentItems.findIndex((g) => g.id === activeLightbox.id);
    const prev = currentItems[(idx - 1 + currentItems.length) % currentItems.length];
    setLightbox(prev.id);
  }, [activeLightbox, currentItems]);

  const handleNext = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeLightbox == null) return;
    const idx = currentItems.findIndex((g) => g.id === activeLightbox.id);
    const next = currentItems[(idx + 1) % currentItems.length];
    setLightbox(next.id);
  }, [activeLightbox, currentItems]);

  const heights: Record<string, string> = {
    tall: '360px',
    wide: '220px',
    normal: '240px',
  };

  return (
    <section
      id="gallery"
      ref={ref as React.RefObject<HTMLElement>}
      className="py-24 px-4 md:px-8"
      style={{ background: 'linear-gradient(180deg, #0E1218 0%, #0A0F1A 100%)' }}
    >
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className={`mb-10 reveal ${isVisible ? 'visible' : ''}`}>
          <p className="text-accent text-xs font-semibold tracking-[3px] uppercase mb-3">
            Visual Story
          </p>
          <h2 className="font-poppins font-bold text-3xl md:text-5xl text-white">
            Gallery
          </h2>
          <div className="h-1 w-16 bg-accent rounded-full mt-4" />
        </div>

        {/* Tabs */}
        <div className={`flex items-center gap-3 mb-10 reveal ${isVisible ? 'visible' : ''}`}>
          <div
            className="inline-flex items-center p-1 gap-1 rounded-full"
            style={{ background: '#131923', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  setLightbox(null);
                }}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-250 cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-accent text-white'
                    : 'text-text-muted hover:text-white'
                }`}
              >
                <i className={`${tab.icon} text-base`} />
                {tab.label}
                <span
                  className={`ml-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                    activeTab === tab.key
                      ? 'bg-white/20 text-white'
                      : 'bg-white/5 text-text-muted'
                  }`}
                >
                  {tabItems[tab.key].length}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Masonry Grid */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          {currentItems.map((item, i) => (
            <div
              key={item.id}
              className={`relative rounded-2xl overflow-hidden cursor-pointer group break-inside-avoid reveal reveal-delay-${(i % 3) + 1} ${isVisible ? 'visible' : ''}`}
              style={{ height: heights[item.span || 'normal'] }}
              onClick={() => setLightbox(item.id)}
            >
              <img
                src={item.image}
                alt={item.caption}
                className="w-full h-full object-cover object-top transition-transform duration-500"
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.08)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)';
                }}
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                <p className="text-white font-poppins font-semibold text-sm">{item.caption}</p>
              </div>
              {/* Expand icon */}
              <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: 'rgba(14,18,24,0.75)', backdropFilter: 'blur(8px)' }}>
                <i className="ri-expand-diagonal-line text-white text-sm" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {activeLightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.95)' }}
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Close */}
            <button
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center bg-accent rounded-full text-white cursor-pointer z-10 whitespace-nowrap"
              onClick={() => setLightbox(null)}
            >
              <i className="ri-close-line text-xl" />
            </button>

            <img
              src={activeLightbox.image}
              alt={activeLightbox.caption}
              className="w-full rounded-2xl object-contain max-h-[80vh]"
            />
            <p className="text-center text-text-soft mt-4 text-sm">{activeLightbox.caption}</p>

            {/* Prev */}
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full text-white cursor-pointer border border-white/10 whitespace-nowrap"
              style={{ background: 'rgba(14,18,24,0.8)' }}
              onClick={handlePrev}
            >
              <i className="ri-arrow-left-line" />
            </button>

            {/* Next */}
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full text-white cursor-pointer border border-white/10 whitespace-nowrap"
              style={{ background: 'rgba(14,18,24,0.8)' }}
              onClick={handleNext}
            >
              <i className="ri-arrow-right-line" />
            </button>

            {/* Counter */}
            <div
              className="absolute bottom-14 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-xs text-white/70 whitespace-nowrap"
              style={{ background: 'rgba(14,18,24,0.7)' }}
            >
              {currentItems.findIndex((g) => g.id === activeLightbox.id) + 1} / {currentItems.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
